import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Boxes, Layers, Package, Ruler, Scale, Wrench, TrendingUp, Cog } from "lucide-react";

const StorePredictor = () => {
  const [progress, setProgress] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  const [boxRotation, setBoxRotation] = useState(0);

  const features = [
    { icon: Scale, label: "Weight Prediction", desc: "Auto-calculate board weight from GSM, reel size & cutting specs", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
    { icon: Ruler, label: "Smart Stock Matching", desc: "Match your box requirements to closest available raw materials", color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/30" },
    { icon: Layers, label: "Ply Configuration", desc: "3-ply, 5-ply, 7-ply auto-suggestions based on product weight", color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/30" },
    { icon: Package, label: "Wastage Optimizer", desc: "Minimize corrugation waste with intelligent cutting plans", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" },
    { icon: TrendingUp, label: "Consumption Forecast", desc: "Predict raw material needs from upcoming work orders", color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30" },
    { icon: Cog, label: "Machine Integration", desc: "Auto-suggest machine settings based on board specifications", color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/30" },
  ];

  // Progress animation
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => (prev >= 68 ? 68 : prev + 0.3));
    }, 80);
    return () => clearInterval(interval);
  }, []);

  // Feature highlight cycle
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [features.length]);

  // Box rotation animation
  useEffect(() => {
    const interval = setInterval(() => {
      setBoxRotation((prev) => prev + 0.5);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-gray-900 via-emerald-950/30 to-gray-900 p-8">
        {/* Animated background pattern - corrugation waves */}
        <div className="absolute inset-0 opacity-[0.03]">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-full border-t border-emerald-400"
              style={{
                top: `${i * 5 + 2}%`,
                transform: `scaleY(${Math.sin(i * 0.5) * 0.5 + 1})`,
                opacity: 0.5 + Math.sin(i * 0.3) * 0.3,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 flex flex-col items-center text-center">
          {/* Animated 3D Box */}
          <div className="relative w-32 h-32 mb-6" style={{ perspective: "400px" }}>
            <div
              className="w-full h-full flex items-center justify-center"
              style={{
                transform: `rotateY(${boxRotation}deg) rotateX(10deg)`,
                transformStyle: "preserve-3d",
              }}
            >
              <Boxes className="w-20 h-20 text-emerald-400 drop-shadow-[0_0_15px_rgba(52,211,153,0.4)]" />
            </div>
            {/* Floating particles */}
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full bg-emerald-400/60"
                style={{
                  top: `${30 + Math.sin(boxRotation * 0.02 + i * 1.2) * 25}%`,
                  left: `${30 + Math.cos(boxRotation * 0.02 + i * 1.2) * 25}%`,
                  opacity: 0.3 + Math.sin(boxRotation * 0.03 + i) * 0.3,
                  transform: `scale(${0.5 + Math.sin(boxRotation * 0.02 + i) * 0.5})`,
                }}
              />
            ))}
          </div>

          <h1 className="text-3xl font-bold text-white mb-2">
            Stock Predictor
          </h1>
          <p className="text-emerald-400 font-semibold text-lg mb-1">Under Development</p>
          <p className="text-gray-400 max-w-lg">
            We're building an intelligent prediction engine for your corrugated box manufacturing workflow. Smart matching, waste reduction, and forecasting — all in one place.
          </p>

          {/* Progress Bar */}
          <div className="w-full max-w-md mt-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-400">Development Progress</span>
              <span className="text-sm font-mono font-bold text-emerald-400">{Math.round(progress)}%</span>
            </div>
            <div className="h-3 bg-gray-800 rounded-full overflow-hidden border border-gray-700">
              <div
                className="h-full bg-gradient-to-r from-emerald-600 via-emerald-500 to-cyan-400 rounded-full transition-all duration-300 relative"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
              </div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Core Engine</span>
              <span>Stock Matching</span>
              <span>Forecasting</span>
            </div>
          </div>
        </div>
      </div>

      {/* Features Coming Soon */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Wrench className="h-5 w-5 text-emerald-400" />
          Features Being Built
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            const isActive = idx === activeFeature;
            return (
              <Card
                key={idx}
                className={`transition-all duration-500 cursor-default ${
                  isActive
                    ? `${feature.bg} ${feature.border} border-2 shadow-lg scale-[1.02]`
                    : "bg-gray-900/50 border-gray-700/50 hover:border-gray-600"
                }`}
                data-testid={`predictor-feature-${idx}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isActive ? feature.bg : "bg-gray-800"}`}>
                      <Icon className={`h-5 w-5 ${isActive ? feature.color : "text-gray-500"}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-semibold text-sm ${isActive ? feature.color : "text-gray-300"}`}>
                        {feature.label}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">{feature.desc}</p>
                    </div>
                    {isActive && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 animate-pulse">
                        In Progress
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Industry Context */}
      <Card className="bg-gray-900/50 border-gray-700/50">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-500/10 rounded-xl border border-amber-500/20">
              <Package className="h-8 w-8 text-amber-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Built for Corrugated Box Manufacturing</h3>
              <p className="text-gray-400 text-sm mt-1">
                This predictor understands flute types, ply configurations, GSM calculations, reel-to-board weight conversion, and joint multipliers specific to the corrugated packaging industry.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StorePredictor;
